import { useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import {
  Box,
  Flex,
  FlexProps,
  Text,
  Tooltip,
  tokenPrefix,
} from '@hivewatch/dark'

import { formatDateTimeWithAt } from '@/utils/formatDateTime'

interface NotificationSubtitleIProps extends FlexProps {
  timestamp: string
  sourceName?: string
  facilityShortName?: string
  facilityName?: string
}

export const NotificationSubtitle = ({
  timestamp,
  sourceName,
  facilityShortName,
  facilityName,
  ...rest
}: NotificationSubtitleIProps) => {
  const mode = useLightMode()
  const p = tokenPrefix(mode)
  const [facilityAnchorEl, setFacilityAnchorEl] = useState<HTMLElement | null>(
    null
  )

  const separator = (
    <Box bg={`${p}border.subtle`} height='0.85em' mx={3} w='1px' />
  )

  return (
    <Flex
      alignItems='center'
      color={`${p}text.secondary`}
      data-testid='NotificationSubtitle:details'
      direction='row'
      maxW='300px'
      // Soehne (Söhne Kräftig, 11/16) — matches the rebranded card titles.
      textStyle='small-body-bold'
      w='full'
      {...rest}
    >
      <Text lineClamp={1} maxW='40%'>
        {formatDateTimeWithAt(timestamp)}
      </Text>
      {sourceName && (
        <Flex alignItems='center' maxW='45%'>
          {separator}
          <Text lineClamp={1}>{sourceName}</Text>
        </Flex>
      )}
      {facilityShortName && (
        <Flex alignItems='center'>
          {separator}
          {/* Only wire the tooltip when there's a full facility name to show,
              otherwise hovering the short name opens an empty tooltip. */}
          <Text
            lineClamp={1}
            {...(facilityName && {
              onMouseEnter: (e: React.MouseEvent<HTMLElement>) =>
                setFacilityAnchorEl(e.currentTarget),
              onMouseLeave: () => setFacilityAnchorEl(null),
            })}
          >
            {facilityShortName}
          </Text>
          {facilityName && (
            <Tooltip
              anchorEl={facilityAnchorEl}
              colorScheme={mode}
              label={facilityName}
              placement='top'
            />
          )}
        </Flex>
      )}
    </Flex>
  )
}
